import React from 'react';
import { Box, Typography } from '@mui/material';
import { FolderOpen as FolderOpenIcon } from '@mui/icons-material';
import { EnhancedProjectFile } from '../../types/file.types';
import FileCard from './cards/FileCard';

/**
 * FileGridView Props
 */
interface FileGridViewProps {
  /** 표시할 파일 목록 (이미 필터링됨) */
  files: EnhancedProjectFile[];
  /** 선택된 파일 ID 목록 */
  selectedFiles: string[];
  /** 파일 선택 핸들러 */
  onFileSelect: (fileId: string, selected: boolean) => void;
  /** 파일 클릭 핸들러 (미리보기용 - 향후 구현) */
  onFileClick?: (file: EnhancedProjectFile) => void;
  /** 편집 버튼 핸들러 (상세 패널 열기) */
  onFileEdit?: (file: EnhancedProjectFile) => void;
  /** 다운로드 핸들러 (선택적) */
  onFileDownload?: (file: EnhancedProjectFile) => void;
  /** 삭제 핸들러 (선택적) */
  onFileDelete?: (file: EnhancedProjectFile) => void;
  /** 즐겨찾기 토글 핸들러 (선택적) */
  onToggleFavorite?: (file: EnhancedProjectFile) => void;
  /** 검색어 (빈 상태 메시지용) */
  searchTerm?: string;
}

/**
 * 파일 그리드 뷰 컴포넌트
 * 
 * @description
 * 파일을 카드 형태의 그리드로 표시
 * 
 * @layout
 * - xs: 1열, sm: 2열, md: 3열, lg: 4열 (반응형)
 * - gap: 16px
 * 
 * @performance
 * - FileCard에 React.memo 적용으로 최적화
 * - 변경된 카드만 리렌더링
 */
export default function FileGridView({
  files,
  selectedFiles,
  onFileSelect,
  onFileClick,
  onFileEdit,
  onFileDownload,
  onFileDelete,
  onToggleFavorite,
  searchTerm = '',
}: FileGridViewProps) {
  // 빈 상태
  if (files.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
        }}
      >
        <FolderOpenIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">
          {searchTerm ? '검색 결과가 없습니다' : '파일이 없습니다'}
        </Typography>
        {searchTerm && (
          <Typography variant="body2" color="text.disabled">
            다른 검색어로 시도해보세요
          </Typography>
        )}
      </Box>
    );
  }

  // Grid 렌더링
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {files.map((file) => (
        <FileCard
          key={file.id || ''}
          file={file}
          selected={selectedFiles.includes(file.id || '')}
          onSelect={onFileSelect}
          onClick={onFileClick}
          onEdit={onFileEdit}
          onDownload={onFileDownload}
          onDelete={onFileDelete}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </Box>
  );
}
